import { createTheme } from "@mui/material/styles";

const COLORS = {
  light: {
    accent: "#073AFF",
    accentHover: "#214EF0",
    onAccent: "#FFFFFF",
    background: "#FFFFFF",
    surface: "#FFFFFF",
    text: "#24272D",
    muted: "#687283",
    border: "#E0E3E8",
    success: "#2E7D32",
    danger: "#C62828",
    warning: "#806326",
    gold: "#E5CE8E",
    onGold: "#3D3117",
    onSemantic: "#FFFFFF",
  },
  dark: {
    accent: "#6988FF",
    accentHover: "#7D98FF",
    onAccent: "#101A3D",
    background: "#14161B",
    surface: "#1C1F25",
    text: "#ECEDF0",
    muted: "#A1A7B3",
    border: "#353A45",
    success: "#4CAF50",
    danger: "#EF5350",
    warning: "#E5CE8E",
    gold: "#E5CE8E",
    onGold: "#3D3117",
    onSemantic: "#171B18",
  },
};

// MUI needs resolved colors for its alpha/contrast calculations; CSS variables
// remain the source for surfaces and field states at render time.
export function createBrandMuiTheme(mode = "light", colors = {}) {
  const c = { ...COLORS[mode === "dark" ? "dark" : "light"], ...colors };
  const focusRing = {
    outline: "2px solid var(--color-accent)",
    outlineOffset: 2,
  };
  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.accent,
        dark: c.accentHover,
        contrastText: c.onAccent,
      },
      secondary: {
        main: c.gold,
        dark: "#C5AB6E",
        contrastText: c.onGold,
      },
      success: { main: c.success, contrastText: c.onSemantic },
      error: { main: c.danger, contrastText: c.onSemantic },
      warning: {
        main: c.warning,
        contrastText: mode === "dark" ? c.onSemantic : "#FFFFFF",
      },
      info: { main: c.muted },
      background: { default: c.background, paper: c.surface },
      text: { primary: c.text, secondary: c.muted },
      divider: c.border,
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
      fontSize: 14,
      button: { textTransform: "none", fontWeight: 600, lineHeight: 1.45 },
      h6: { fontSize: 20, fontWeight: 650, lineHeight: 1.4 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 40,
            padding: "8px 16px",
            borderRadius: "var(--btn-radius, 10px)",
            boxShadow: "none",
            transition: "var(--btn-transition)",
            "&:focus-visible": focusRing,
            "&.Mui-disabled": {
              color: "var(--color-text-muted)",
              cursor: "not-allowed",
            },
            "@media (max-width: 600px)": {
              minHeight: 44,
              maxWidth: "100%",
              whiteSpace: "normal",
            },
          },
          sizeSmall: { minHeight: 36, padding: "6px 12px", fontSize: 13 },
          sizeLarge: { minHeight: 48, padding: "10px 20px", fontSize: 15 },
          contained: {
            "&.Mui-disabled": {
              backgroundColor: "var(--color-input-bg-disabled)",
            },
          },
          outlined: {
            "&.Mui-disabled": { borderColor: "var(--color-border)" },
          },
          textSecondary: { color: "var(--color-gold-ink)" },
          outlinedSecondary: { color: "var(--color-gold-ink)", borderColor: "var(--color-gold-ink)" },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: 10, "&:focus-visible": focusRing },
        },
      },
      MuiButtonBase: {
        styleOverrides: { root: { "&.Mui-focusVisible": focusRing } },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none", color: "var(--color-text)" },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: "1px solid var(--color-border)",
            backgroundColor: "var(--color-card)",
            borderRadius: 16,
            boxShadow: "var(--shadow-lg)",
            "@media (max-width: 600px)": {
              margin: 16,
              width: "calc(100% - 32px)",
              maxWidth: "calc(100% - 32px)",
              maxHeight: "calc(100% - 32px)",
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "24px 24px 12px",
            color: "var(--color-text)",
            overflowWrap: "anywhere",
            "@media (max-width: 600px)": {
              padding: "20px 20px 12px",
              fontSize: 18,
            },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "12px 24px 24px",
            color: "var(--color-text)",
            ".MuiDialogTitle-root + &": { paddingTop: 12 },
            "@media (max-width: 600px)": { padding: "12px 20px 20px" },
          },
          dividers: { borderColor: "var(--color-border)" },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "16px 24px 20px",
            gap: 8,
            flexWrap: "wrap",
            backgroundColor: "var(--color-surface)",
            "& > :not(style) ~ :not(style)": { marginLeft: 0 },
            "@media (max-width: 600px)": {
              padding: "16px 20px",
              "& .MuiButton-root": { flex: "1 1 auto" },
            },
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: { root: { backgroundColor: "var(--color-overlay)" } },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            border: "1px solid var(--color-border)",
            backgroundColor: "var(--color-card)",
            boxShadow: "var(--shadow-lg)",
            borderRadius: 12,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: 40,
            whiteSpace: "normal",
            "&.Mui-selected": { backgroundColor: "var(--color-accent-soft)" },
            "&.Mui-selected:hover": {
              backgroundColor: "var(--color-accent-light)",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: 44,
            borderRadius: "var(--input-radius, 10px)",
            backgroundColor: "var(--color-input-bg)",
            color: "var(--color-input-text)",
            transition: "var(--input-transition)",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--color-input-border-hover)",
            },
            "&.Mui-focused": { boxShadow: "var(--input-shadow-focus)" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1,
              borderColor: "var(--color-accent)",
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--color-danger)",
            },
            "&.Mui-error.Mui-focused": {
              boxShadow: "var(--input-shadow-error)",
            },
            "&.Mui-disabled": {
              backgroundColor: "var(--color-input-bg-disabled)",
            },
            "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--color-border)",
            },
          },
          notchedOutline: { borderColor: "var(--color-input-border)" },
          input: {
            padding: "12px 14px",
            "&::placeholder": {
              color: "var(--color-input-placeholder)",
              opacity: 1,
            },
            "&.Mui-disabled": {
              WebkitTextFillColor: "var(--color-text-muted)",
            },
            "@media (max-width: 600px)": { fontSize: 16 },
          },
          inputSizeSmall: { padding: "10px 14px" },
          inputMultiline: { padding: 0 },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: "var(--color-input-label)",
            "&.Mui-focused": { color: "var(--color-accent)" },
            "&.Mui-error": { color: "var(--color-danger)" },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            margin: "6px 0 0",
            lineHeight: 1.5,
            color: "var(--color-input-helper)",
            "&.Mui-error": { color: "var(--color-danger)" },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: "var(--color-border)",
            color: "var(--color-text)",
          },
          head: { backgroundColor: "var(--color-surface)", fontWeight: 600 },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: "var(--color-border)" } },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontSize: 12, padding: "8px 12px", borderRadius: 8 },
        },
      },
    },
  });
}
